"use client"

import { useEffect, useState } from "react"
import { X, RefreshCw, Send, Clock, User, Briefcase, Paperclip, Edit2, Trash2, FileText, LayoutDashboard, Palette, Lock, Save, Link as LinkIcon, Loader2, Bell, CheckCircle2, ExternalLink, Download, Search } from "lucide-react"
import { jsPDF } from "jspdf"
import {
    getBriefings,
    updateBriefing,
    deleteBriefing,
    getBriefingComments,
    addBriefingComment,
    getBriefingAttachments,
    getTeamMembers,
    getClient,
    getUserId,
} from "../API"
import BriefingModal from "./BriefingModal"
import BriefingSelect from "./BriefingSelect"
import { markBriefingSeen, isBriefingUnread, countUnread } from "./briefingNotifications"

const STATUS_META: Record<string, { label: string; color: string }> = {
    pendente: { label: "Pendente", color: "bg-yellow-500/20 text-yellow-400" },
    em_producao: { label: "Em produção", color: "bg-blue-500/20 text-blue-400" },
    em_revisao: { label: "Em revisão", color: "bg-purple-500/20 text-purple-400" },
    aprovacao_cliente: { label: "Pendente aprovação do cliente", color: "bg-orange-500/20 text-orange-400" },
    falta_resposta: { label: "Falta de resposta", color: "bg-amber-600/20 text-amber-400" },
    concluido: { label: "Concluído", color: "bg-green-500/20 text-green-400" },
    pausado: { label: "Pausado", color: "bg-gray-500/20 text-gray-400" },
    cancelado: { label: "Cancelado", color: "bg-red-500/20 text-red-400" },
}

// Ordem das colunas no Kanban
const STATUS_ORDER = ["pendente", "em_producao", "em_revisao", "aprovacao_cliente", "falta_resposta", "pausado", "concluido", "cancelado"]

const PRIORITY_META: Record<string, { label: string; color: string }> = {
    baixa: { label: "Baixa", color: "text-primary/40" },
    normal: { label: "Normal", color: "text-primary/60" },
    alta: { label: "Alta", color: "text-orange-400" },
    urgente: { label: "Urgente", color: "text-red-400" },
}

interface Props {
    onClose: () => void
}

export default function BriefingsPage({ onClose }: Props) {
    const [briefings, setBriefings] = useState<any[]>([])
    const [allBriefings, setAllBriefings] = useState<any[]>([]) // sem filtro, usado no dashboard
    const [loading, setLoading] = useState(true)
    const [designerFilter, setDesignerFilter] = useState<string>("todos")
    const [designers, setDesigners] = useState<{ id: string; name: string }[]>([])
    const [selected, setSelected] = useState<any | null>(null)
    const [editingBriefing, setEditingBriefing] = useState<any | null>(null)
    const [showDashboard, setShowDashboard] = useState(false)
    const [dragOverStatus, setDragOverStatus] = useState<string | null>(null)
    const [userRole, setUserRole] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState("")

    const isGestao = userRole === "gestao"
    const isDesigner = userRole === "designer"
    const isVendedor = userRole === "vendedor"
    const myId = getUserId()

    useEffect(() => {
        (async () => {
            try {
                const { getClient } = await import("../API")
                const client = await getClient(getUserId())
                if (client && client[0]) setUserRole(client[0].role || null)
            } catch (e) { console.error("Erro ao buscar cargo:", e) }
        })()
    }, [])

    const load = async () => {
        setLoading(true)
        const filters: any = {}
        if (isGestao && designerFilter !== "todos") {
            filters.designer_id = designerFilter
        } else if (isDesigner) {
            filters.designer_id = myId
        } else if (isVendedor) {
            filters.vendedor_id = myId
        }
        const [data, all] = await Promise.all([
            getBriefings(filters),
            getBriefings({}),
        ])
        setBriefings(data || [])
        setAllBriefings(all || [])
        setLoading(false)
    }

    useEffect(() => { load() }, [designerFilter, userRole])
    useEffect(() => {
        Promise.all([
            getTeamMembers("designer"),
            getTeamMembers("gestao"),
        ]).then(([d, g]) => {
            setDesigners([...(d || []), ...(g || [])])
        })
    }, [])

    const handleDelete = async (id: string) => {
        if (!window.confirm("Deletar este briefing? Essa ação não pode ser desfeita.")) return
        const ok = await deleteBriefing(id)
        if (ok) {
            setBriefings((prev) => prev.filter((b) => b.id !== id))
            setAllBriefings((prev) => prev.filter((b) => b.id !== id))
            if (selected?.id === id) setSelected(null)
        }
    }

    const myRelevantBriefings = isGestao ? allBriefings : allBriefings.filter((b) => b.created_by === myId || b.designer_id === myId)
    const unreadCount = countUnread(myRelevantBriefings)

    const handleSelect = (b: any) => {
        setSelected(b)
        markBriefingSeen(b.id)
    }

    const applyUpdatedBriefing = (updated: any) => {
        markBriefingSeen(updated.id)
        setSelected(updated)
        setBriefings((prev) => prev.map((b) => b.id === updated.id ? updated : b))
        setAllBriefings((prev) => prev.map((b) => b.id === updated.id ? updated : b))
    }

    const handleStatusChange = async (id: string, status: string) => {
        const result = await updateBriefing(id, { status })
        if (result) applyUpdatedBriefing(result)
    }

    const searchLower = searchTerm.toLowerCase()
    const filteredBriefings = searchTerm
        ? briefings.filter((b) =>
            (b.title || "").toLowerCase().includes(searchLower) ||
            (b.client_name || "").toLowerCase().includes(searchLower) ||
            (b.vendedor_name || "").toLowerCase().includes(searchLower) ||
            (b.designer_name || "").toLowerCase().includes(searchLower) ||
            (b.model_name || "").toLowerCase().includes(searchLower) ||
            (b.models || []).some((m: any) => (m.model_name || "").toLowerCase().includes(searchLower))
        )
        : briefings

    // Arrastar card entre colunas (quem criou ou o designer asignado pode mudar o status)
    const handleDrop = (status: string) => {
        setDragOverStatus(null)
        const draggedId = (window as any).__draggedBriefingId
        if (!draggedId) return
        const b = briefings.find((x) => x.id === draggedId)
        if (!b || b.status === status) return
        const isCreator = b.created_by === myId
        const isAssignedDesigner = b.designer_id === myId
        if (!isCreator && !isAssignedDesigner && !isGestao) return
        handleStatusChange(draggedId, status)
    }

    return (
        <div className="absolute inset-0 z-[9999] flex flex-col bg-secondary text-primary">
            {/* Header */}
            <header className="flex items-center gap-3 px-5 py-3 border-b border-white/10 bg-background flex-shrink-0">
                <h1 className="text-lg font-semibold flex items-center gap-2">
                    Briefings
                    {unreadCount > 0 && (
                        <span className="flex items-center gap-1 rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white">
                            <Bell className="h-3 w-3" /> {unreadCount}
                        </span>
                    )}
                </h1>
                <div className="flex-1" />

                {isGestao && (
                    <BriefingSelect
                        value={designerFilter}
                        onChange={setDesignerFilter}
                        className="w-52"
                        options={[{ value: "todos", label: "Todos os designers" }, ...designers.map((d) => ({ value: d.id, label: d.name }))]}
                    />
                )}

                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-primary/40" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar demanda..."
                        className="w-52 rounded-lg border border-white/10 bg-white/5 pl-8 pr-3 py-2 text-sm text-primary placeholder:text-primary/40 outline-none focus:border-blue-500/50 transition-colors"
                    />
                </div>

                <button
                    onClick={() => setShowDashboard((v) => !v)}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${showDashboard ? "border-blue-500/40 bg-blue-600/10 text-blue-400" : "border-white/10 text-primary/70 hover:text-primary hover:bg-white/5"}`}
                >
                    <LayoutDashboard className="h-4 w-4" /> Dashboard
                </button>

                <button onClick={load} className="rounded-lg border border-white/10 p-2 text-primary/70 hover:text-primary hover:bg-white/5 transition-colors">
                    <RefreshCw className="h-4 w-4" />
                </button>

                <button onClick={onClose} className="rounded-full p-1.5 hover:bg-white/5 transition-colors">
                    <X className="h-5 w-5" />
                </button>
            </header>

            {showDashboard && <BriefingsDashboard briefings={allBriefings} />}

            {/* Kanban */}
            <div className="flex-1 overflow-x-auto overflow-y-hidden">
                {loading ? (
                    <p className="p-6 text-sm text-primary/50">Carregando briefings...</p>
                ) : (
                    <div className="flex h-full gap-3 p-4 min-w-max">
                        {STATUS_ORDER.map((status) => {
                            const meta = STATUS_META[status]
                            const cards = filteredBriefings.filter((b) => b.status === status)
                            return (
                                <div
                                    key={status}
                                    onDragOver={(e) => { e.preventDefault(); setDragOverStatus(status) }}
                                    onDragLeave={() => setDragOverStatus((s) => (s === status ? null : s))}
                                    onDrop={(e) => { e.preventDefault(); handleDrop(status) }}
                                    className={`flex flex-col w-[280px] flex-shrink-0 rounded-lg border bg-background/40 transition-colors ${dragOverStatus === status ? "border-blue-500/50 bg-blue-500/5" : "border-white/10"}`}
                                >
                                    <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-white/10 flex-shrink-0">
                                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.color}`}>{meta.label}</span>
                                        <span className="text-xs text-primary/40">{cards.length}</span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                                        {cards.length === 0 && (
                                            <p className="text-[11px] text-primary/30 text-center py-4">Nenhum briefing aqui</p>
                                        )}
                                        {cards.map((b) => {
                                            const draggable = b.created_by === myId
                                            return (
                                                <button
                                                    key={b.id}
                                                    draggable={draggable}
                                                    onDragStart={() => { (window as any).__draggedBriefingId = b.id }}
                                                    onDragEnd={() => { (window as any).__draggedBriefingId = null; setDragOverStatus(null) }}
                                                    onClick={() => handleSelect(b)}
                                                    className={`relative w-full text-left rounded-lg border p-3 transition-colors bg-white/[0.03] hover:bg-white/[0.06] ${selected?.id === b.id ? "border-blue-500/50" : "border-white/10"} ${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
                                                >
                                                    {isBriefingUnread(b) && (
                                                        <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500" />
                                                    )}
                                                    <div className="flex gap-2">
                                                        {(b.models?.length ? b.models[0]?.model_thumb : b.model_thumb) ? (
                                                            <img src={b.models?.length ? b.models[0]?.model_thumb : b.model_thumb} alt="" className="h-10 w-10 rounded-md object-contain bg-black/20 flex-shrink-0" />
                                                        ) : (
                                                            <div className="h-10 w-10 rounded-md bg-black/20 flex-shrink-0" />
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-xs font-semibold text-primary pr-3 line-clamp-2">{b.title}</p>
                                                            <p className="text-[11px] text-primary/40 mt-0.5 truncate">
                                                                {b.models?.length
                                                                    ? (b.models.length > 1 ? `${b.models.length} modelagens` : b.models[0]?.model_name || "Sem modelagem")
                                                                    : (b.model_name || "Sem modelagem")
                                                                }
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-2 text-[11px] text-primary/40">
                                                        {b.client_name && <span className="flex items-center gap-1 truncate"><User className="h-2.5 w-2.5 flex-shrink-0" />{b.client_name}</span>}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1 text-[11px] text-primary/40">
                                                        {b.designer_name && <span className="flex items-center gap-1 truncate"><Palette className="h-2.5 w-2.5 flex-shrink-0" />{b.designer_name}</span>}
                                                    </div>
                                                    <div className="flex items-center justify-between mt-2">
                                                        {b.deadline ? (
                                                            <span className="flex items-center gap-1 text-[10px] text-primary/40"><Clock className="h-2.5 w-2.5" />{new Date(b.deadline + "T00:00:00").toLocaleDateString("pt-BR")}</span>
                                                        ) : <span />}
                                                        <span className={`text-[10px] font-medium ${PRIORITY_META[b.priority]?.color}`}>{PRIORITY_META[b.priority]?.label}</span>
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* Painel de detalhe: abre como drawer por cima do quadro */}
            {selected && (
                <>
                    <div className="fixed inset-0 z-10 bg-black/40" onClick={() => setSelected(null)} />
                    <div className="fixed right-0 top-0 z-20 h-full w-[440px] max-w-full bg-secondary border-l border-white/10 shadow-2xl overflow-hidden flex flex-col">
                        <BriefingDetail
                            briefing={selected}
                            onClose={() => setSelected(null)}
                            onCloseAll={onClose}
                            onEdit={() => setEditingBriefing(selected)}
                            onDelete={() => handleDelete(selected.id)}
                            onStatusChange={(status) => handleStatusChange(selected.id, status)}
                            onDesignerAreaSaved={applyUpdatedBriefing}
                        />
                    </div>
                </>
            )}

            {editingBriefing && (
                <BriefingModal
                    briefing={editingBriefing}
                    onClose={() => setEditingBriefing(null)}
                    onSaved={(updated) => {
                        setBriefings((prev) => prev.map((b) => b.id === updated.id ? updated : b))
                        setAllBriefings((prev) => prev.map((b) => b.id === updated.id ? updated : b))
                        setSelected(updated)
                    }}
                />
            )}
        </div>
    )
}

// ── Painel de detalhe: dados do briefing + anexos + comentários ────────────
function BriefingDetail({ briefing, onClose, onCloseAll, onEdit, onDelete, onStatusChange, onDesignerAreaSaved }: {
    briefing: any
    onClose: () => void
    onCloseAll: () => void
    onEdit: () => void
    onDelete: () => void
    onStatusChange: (status: string) => void
    onDesignerAreaSaved: (updated: any) => void
}) {
    const [comments, setComments] = useState<any[]>([])
    const [attachments, setAttachments] = useState<any[]>([])
    const [newComment, setNewComment] = useState("")
    const [sending, setSending] = useState(false)
    const [detailUserRole, setDetailUserRole] = useState<string | null>(null)

    const myId = getUserId()
    const isCreator = briefing.created_by === myId
    const isAssignedDesigner = !!briefing.designer_id && briefing.designer_id === myId
    const isGestao = detailUserRole === "gestao"
    const isVendedor = detailUserRole === "vendedor"
    const canEdit = isCreator || isGestao
    const canChangeStatus = !isVendedor && (isCreator || isGestao || isAssignedDesigner)

    const VENDEDOR_STATUSES = ["pendente", "aprovacao_cliente", "falta_resposta", "pausado", "cancelado"]
    const availableStatuses = isVendedor
        ? Object.entries(STATUS_META).filter(([k]) => VENDEDOR_STATUSES.includes(k))
        : Object.entries(STATUS_META)

    const load = async () => {
        const [c, a] = await Promise.all([
            getBriefingComments(briefing.id),
            getBriefingAttachments(briefing.id),
        ])
        setComments(c || [])
        setAttachments(a || [])
    }

    useEffect(() => { load() }, [briefing.id])

    useEffect(() => {
        (async () => {
            try {
                const { getClient } = await import("../API")
                const client = await getClient(getUserId())
                if (client && client[0]) setDetailUserRole(client[0].role || null)
            } catch (e) { console.error("Erro ao buscar cargo:", e) }
        })()
    }, [])

    const handleSend = async () => {
        if (!newComment.trim()) return
        setSending(true)
        try {
            const result = await addBriefingComment(briefing.id, newComment.trim())
            if (result) {
                setComments((prev) => [...prev, result])
                setNewComment("")
            }
        } finally {
            setSending(false)
        }
    }

    // Abre a modelagem vinculada direto no editor 3D (mesma chamada usada pelo
    // "Visualizar" na tela de Modelagens), evitando o designer ter que buscá-la.
    const openModelagem = (b: any, specificModel?: any) => {
        const model = specificModel || b
        if (!model.model_path) return
        const w = window as any
        if (w.$) w.$("#model-title span").text(model.model_name || "")
        w.add_mode = false
        const svgPath = model.model_path.replace(".glb", ".svg")
        w["loadParts"]?.(null, model.model_path, true, 1, false, true, svgPath, model.model_id)
        onCloseAll()
    }

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header do painel */}
            <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-semibold text-primary">{briefing.title}</h2>
                        {!isVendedor && (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_META[briefing.status]?.color || ""}`}>
                            {STATUS_META[briefing.status]?.label || briefing.status}
                        </span>
                        )}
                    </div>
                    <p className="text-xs text-primary/50 mt-0.5">
                        {briefing.models?.length
                            ? (briefing.models.length > 1 ? `${briefing.models.length} modelagens` : briefing.models[0]?.model_name || briefing.model_name || "")
                            : (briefing.model_name || "")
                        }
                        {briefing.client_name ? ` · Cliente: ${briefing.client_name}` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        onClick={() => generateBriefingPDF(briefing)}
                        className="rounded-lg border border-white/10 p-2 text-primary/60 hover:text-primary hover:bg-white/5 transition-colors"
                        title="Baixar PDF"
                    >
                        <Download className="h-4 w-4" />
                    </button>
                    {canEdit ? (
                        <>
                            <button onClick={onEdit} className="rounded-lg border border-white/10 p-2 text-primary/60 hover:text-primary hover:bg-white/5 transition-colors">
                                <Edit2 className="h-4 w-4" />
                            </button>
                            <button onClick={onDelete} className="rounded-lg border border-white/10 p-2 text-red-400 hover:bg-red-500/10 transition-colors">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </>
                    ) : (
                        <span className="flex items-center gap-1 text-[10px] text-primary/40 rounded-full border border-white/10 px-2 py-1">
                            <Lock className="h-3 w-3" /> {isAssignedDesigner ? "Somente o vendedor edita" : "Somente visualização"}
                        </span>
                    )}
                    <button onClick={onClose} className="rounded-lg border border-white/10 p-2 text-primary/60 hover:text-primary hover:bg-white/5 transition-colors">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Abrir a modelagem vinculada direto no editor, sem precisar buscar */}
            {(briefing.models?.length ? briefing.models : [{ model_thumb: briefing.model_thumb, model_name: briefing.model_name, model_path: briefing.model_path }]).map((m: any, idx: number) => (
                m.model_path && (
                    <div key={idx} className="flex items-center gap-3 mx-4 mt-3 rounded-lg border border-white/10 bg-white/5 p-2.5">
                        {m.model_thumb && (
                            <img src={m.model_thumb} alt={m.model_name} className="h-10 w-10 rounded-md object-contain bg-black/20 flex-shrink-0" />
                        )}
                        <p className="text-xs text-primary/70 truncate flex-1">{m.model_name || "Modelagem vinculada"}</p>
                        <button
                            onClick={() => openModelagem(briefing, m)}
                            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors flex-shrink-0"
                        >
                            <ExternalLink className="h-3 w-3" /> Abrir modelagem
                        </button>
                    </div>
                )
            ))}

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Ações do vendedor quando pendente aprovação do cliente */}
                {isVendedor && briefing.status === "aprovacao_cliente" && (
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Resposta do cliente</p>
                    <div className="flex flex-wrap gap-1.5">
                        {[
                            { key: "concluido", label: "Aprovado", color: "bg-green-500/20 text-green-400 hover:bg-green-500/30" },
                            { key: "em_revisao", label: "Revisar", color: "bg-purple-500/20 text-purple-400 hover:bg-purple-500/30" },
                            { key: "pausado", label: "Pausado", color: "bg-gray-500/20 text-gray-400 hover:bg-gray-500/30" },
                            { key: "cancelado", label: "Cancelado", color: "bg-red-500/20 text-red-400 hover:bg-red-500/30" },
                        ].map(opt => (
                            <button
                                key={opt.key}
                                onClick={() => onStatusChange(opt.key)}
                                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${opt.color}`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                )}

                {/* Status rápido */}
                {!isVendedor && (
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Status</p>
                    {canChangeStatus ? (
                        <div className="flex flex-wrap gap-1.5">
                            {availableStatuses.map(([k, v]) => (
                                <button
                                    key={k}
                                    onClick={() => onStatusChange(k)}
                                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${briefing.status === k ? v.color : "text-primary/40 bg-white/5 hover:text-primary"}`}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_META[briefing.status]?.color}`}>
                            {STATUS_META[briefing.status]?.label || briefing.status}
                        </span>
                    )}
                </div>
                )}

                {/* Ficha técnica */}
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Ficha técnica</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <Info label="Contato" value={briefing.client_contact} />
                        <Info label="Vendedor" value={briefing.vendedor_name} />
                        <Info label="Designer" value={briefing.designer_name} />
                        <Info label="Escudo" value={briefing.collar} />
                        <Info label="Manga" value={briefing.sleeve} />
                        <Info label="Tecido" value={briefing.fabric} />
                        <Info label="Tamanhos" value={briefing.sizes} />
                        <Info label="Quantidade" value={briefing.quantity} />
                        <Info label="Prazo" value={briefing.deadline ? new Date(briefing.deadline + "T00:00:00").toLocaleDateString("pt-BR") : null} />
                    </div>
                    {briefing.description && (
                        <p className="text-xs text-primary/70 bg-white/5 rounded-md p-3 mt-2 whitespace-pre-wrap">{briefing.description}</p>
                    )}
                </div>

                {/* Área do designer: dúvidas/ajustes + link do projeto — só o designer responsável edita */}
                <DesignerArea briefing={briefing} editable={isAssignedDesigner || isGestao} onSaved={onDesignerAreaSaved} />

                {/* Anexos */}
                {attachments.length > 0 && (
                    <div>
                        <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <Paperclip className="h-3 w-3" /> Anexos
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                            {attachments.map((att) => (
                                <a key={att.id} href={att.file_url} target="_blank" rel="noreferrer"
                                    className="rounded-lg border border-white/10 bg-white/5 p-2 hover:border-white/25 transition-colors">
                                    {/\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(att.file_name || "") ? (
                                        <img src={att.file_url} alt={att.file_name} className="h-14 w-full object-contain" />
                                    ) : (
                                        <div className="h-14 w-full flex items-center justify-center text-primary/50">
                                            <FileText className="h-6 w-6" />
                                        </div>
                                    )}
                                    <p className="text-[9px] text-primary/40 truncate mt-1">{att.file_name}</p>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Comentários / comunicação */}
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Comunicação</p>
                    <div className="space-y-2">
                        {comments.length === 0 && (
                            <p className="text-xs text-primary/40">Nenhuma mensagem ainda. Combine aqui os ajustes entre vendedor e designer.</p>
                        )}
                        {comments.map((c) => (
                            <div key={c.id} className="rounded-lg bg-white/5 p-2.5">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-primary">{c.author_name || "Usuário"}</span>
                                    <span className="text-[10px] text-primary/40">{new Date(c.created_at).toLocaleString("pt-BR")}</span>
                                </div>
                                <p className="text-xs text-primary/70 mt-1 whitespace-pre-wrap">{c.message}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Caixa de envio */}
            <div className="flex items-center gap-2 p-3 border-t border-white/10">
                <input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    placeholder="Escreva uma mensagem para o vendedor ou designer..."
                    className="flex-1 rounded-lg border border-white/10 bg-secondary px-3 py-2 text-sm text-primary focus:outline-none focus:border-blue-500"
                />
                <button
                    onClick={handleSend}
                    disabled={sending || !newComment.trim()}
                    className="flex items-center justify-center rounded-lg bg-blue-600 p-2.5 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                    <Send className="h-4 w-4" />
                </button>
            </div>
        </div>
    )
}

// ── Gerar PDF individual do briefing ──
async function generateBriefingPDF(briefing: any) {
    const doc = new jsPDF()
    const margin = 20
    let y = 20

    const addLine = () => {
        y += 2
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, y, 190, y)
        y += 8
    }

    // Título
    doc.setFont("helvetica", "bold")
    doc.setFontSize(16)
    doc.text(briefing.title || "Briefing", margin, y)
    y += 8

    // Status + Prioridade
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.setTextColor(120, 120, 120)
    const statusLabel = STATUS_META[briefing.status]?.label || briefing.status
    const priorityLabel = PRIORITY_META[briefing.priority]?.label || briefing.priority || "Normal"
    doc.text(`Status: ${statusLabel}  |  Prioridade: ${priorityLabel}`, margin, y)
    y += 6

    if (briefing.deadline) {
        doc.text(`Prazo: ${new Date(briefing.deadline + "T00:00:00").toLocaleDateString("pt-BR")}`, margin, y)
        y += 6
    }

    doc.setTextColor(0, 0, 0)
    addLine()

    // Ficha técnica
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.text("Ficha Técnica", margin, y)
    y += 7

    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)

    const modelsList = briefing.models?.length ? briefing.models : [{ model_name: briefing.model_name, collar: briefing.collar, sleeve: briefing.sleeve, fabric: briefing.fabric, sizes: briefing.sizes, quantity: briefing.quantity }]

    const fields: [string, string | null | undefined][] = [
        ["Modelos", modelsList.length > 1 ? `${modelsList.length} modelagens` : modelsList[0]?.model_name],
        ["Cliente", briefing.client_name],
        ["Contato", briefing.client_contact],
        ["Vendedor", briefing.vendedor_name],
        ["Designer", briefing.designer_name],
    ]

    for (const [label, value] of fields) {
        if (value) {
            doc.setFont("helvetica", "bold")
            doc.text(`${label}:`, margin, y)
            doc.setFont("helvetica", "normal")
            doc.text(String(value), margin + 30, y)
            y += 5
        }
    }

    if (modelsList.length > 1 || (modelsList[0] && (modelsList[0].collar || modelsList[0].sleeve || modelsList[0].fabric))) {
        y += 3
        for (let i = 0; i < modelsList.length; i++) {
            const m = modelsList[i]
            if (modelsList.length > 1) {
                doc.setFont("helvetica", "bold")
                doc.setFontSize(10)
                doc.text(`Modelagem ${i + 1}: ${m.model_name || "-"}`, margin, y)
                y += 5
            }
            const modelFields: [string, string | null | undefined][] = [
                ["Escudo", m.collar],
                ["Manga", m.sleeve],
                ["Tecido", m.fabric],
                ["Tamanhos", m.sizes],
                ["Quantidade", m.quantity],
            ]
            for (const [label, value] of modelFields) {
                if (value) {
                    doc.setFont("helvetica", "normal")
                    doc.setFontSize(9)
                    doc.text(`${label}: ${value}`, margin + (modelsList.length > 1 ? 5 : 0), y)
                    y += 4.5
                }
            }
            y += 2
        }
    }

    if (briefing.description) {
        addLine()
        doc.setFont("helvetica", "bold")
        doc.setFontSize(12)
        doc.text("Descrição", margin, y)
        y += 7
        doc.setFont("helvetica", "normal")
        doc.setFontSize(10)
        const descLines = doc.splitTextToSize(briefing.description, 170)
        doc.text(descLines, margin, y)
        y += descLines.length * 5
    }

    if (briefing.designer_notes || briefing.designer_project_link) {
        addLine()
        doc.setFont("helvetica", "bold")
        doc.setFontSize(12)
        doc.text("Área do Designer", margin, y)
        y += 7
        doc.setFont("helvetica", "normal")
        doc.setFontSize(10)
        if (briefing.designer_notes) {
            const noteLines = doc.splitTextToSize(briefing.designer_notes, 170)
            doc.text(noteLines, margin, y)
            y += noteLines.length * 5
        }
        if (briefing.designer_project_link) {
            y += 2
            doc.setFont("helvetica", "bold")
            doc.text("Link do projeto: ", margin, y)
            doc.setFont("helvetica", "normal")
            doc.setTextColor(0, 100, 200)
            doc.textWithLink(briefing.designer_project_link, margin + 32, y, { url: briefing.designer_project_link })
            doc.setTextColor(0, 0, 0)
            y += 5
        }
    }

    // Anexos
    const attachments = await getBriefingAttachments(briefing.id)
    if (attachments?.length) {
        addLine()
        doc.setFont("helvetica", "bold")
        doc.setFontSize(12)
        doc.text("Anexos", margin, y)
        y += 7

        const ATTACHMENT_LABELS: Record<string, string> = {
            logo: "Logos",
            patrocinador: "Patrocinadores",
            referencia: "Referências",
            outro: "Arquivos diversos",
        }

        const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|bmp)$/i

        const grouped: Record<string, typeof attachments> = {}
        for (const att of attachments) {
            const kind = att.kind || "outro"
            if (!grouped[kind]) grouped[kind] = []
            grouped[kind].push(att)
        }

        for (const [kind, items] of Object.entries(grouped)) {
            const label = ATTACHMENT_LABELS[kind] || kind
            doc.setFont("helvetica", "bold")
            doc.setFontSize(10)
            doc.text(label, margin, y)
            y += 5

            for (const att of items) {
                if (y > 260) {
                    doc.addPage()
                    y = 20
                }

                const isImage = IMAGE_EXTS.test(att.file_name || att.file_url || "")

                if (isImage && att.file_url) {
                    try {
                        const imgData = await fetch(att.file_url).then(r => r.blob()).then(blob => new Promise<string>((resolve) => {
                            const reader = new FileReader()
                            reader.onloadend = () => resolve(reader.result as string)
                            reader.readAsDataURL(blob)
                        }))

                        const img = new Image()
                        await new Promise<void>((resolve) => {
                            img.onload = () => resolve()
                            img.src = imgData
                        })

                        const maxW = 170
                        const maxH = 60
                        let w = img.width
                        let h = img.height
                        if (w > maxW) { h = (maxW / w) * h; w = maxW }
                        if (h > maxH) { w = (maxH / h) * w; h = maxH }

                        if (y + h + 5 > 260) {
                            doc.addPage()
                            y = 20
                        }

                        doc.setFont("helvetica", "normal")
                        doc.setFontSize(8)
                        doc.setTextColor(100, 100, 100)
                        doc.text(att.file_name || "imagem", margin + 5, y)
                        y += 4

                        doc.addImage(imgData, "JPEG", margin + 5, y, w, h)
                        y += h + 6
                    } catch {
                        doc.setFont("helvetica", "normal")
                        doc.setFontSize(9)
                        doc.setTextColor(180, 0, 0)
                        doc.text(`${att.file_name || "arquivo"} (falha ao carregar imagem)`, margin + 5, y)
                        doc.setTextColor(0, 0, 0)
                        y += 5
                    }
                } else {
                    doc.setFont("helvetica", "normal")
                    doc.setFontSize(9)
                    doc.setTextColor(60, 60, 60)
                    doc.text(`${att.file_name || "arquivo"}`, margin + 5, y)
                    if (att.file_url) {
                        y += 4
                        doc.setFontSize(7)
                        doc.setTextColor(0, 100, 200)
                        doc.textWithLink(att.file_url, margin + 5, y, { url: att.file_url })
                        doc.setTextColor(0, 0, 0)
                    }
                    y += 5
                }
            }
        }
    }

    // Rodapé
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")} · Aisten`, margin, 285)
        doc.text(`Página ${i}/${pageCount}`, 190, 285, { align: "right" })
    }

    const filename = `briefing-${(briefing.title || "briefing").replace(/[^a-zA-Z0-9]/g, "_").toLowerCase()}.pdf`
    doc.save(filename)
}

// ── Gerar PDF resumo do dashboard ──
function generateDashboardPDF(briefings: any[]) {
    const doc = new jsPDF()
    const margin = 20
    let y = 20

    const addLine = () => {
        y += 2
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, y, 190, y)
        y += 8
    }

    const checkPage = (needed = 15) => {
        if (y + needed > 275) {
            doc.addPage()
            y = 20
        }
    }

    // Título
    doc.setFont("helvetica", "bold")
    doc.setFontSize(18)
    doc.text("Resumo de Briefings", margin, y)
    y += 8
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.setTextColor(120, 120, 120)
    const totalModels = briefings.reduce((s, b) => s + (b.models?.length || 1), 0)
    doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")} · Total: ${totalModels} modelagens`, margin, y)
    doc.setTextColor(0, 0, 0)
    y += 8

    addLine()

    // Status counts
    doc.setFont("helvetica", "bold")
    doc.setFontSize(13)
    doc.text("Por Status", margin, y)
    y += 7
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)

    for (const [key, meta] of Object.entries(STATUS_META)) {
        const filtered = briefings.filter((b) => b.status === key)
        const count = filtered.reduce((s, b) => s + (b.models?.length || 1), 0)
        if (count > 0) {
            checkPage()
            doc.setFont("helvetica", "bold")
            doc.text(`${meta.label}:`, margin, y)
            doc.setFont("helvetica", "normal")
            doc.text(String(count), margin + 50, y)
            y += 5
        }
    }

    addLine()

    // Por prioridade
    doc.setFont("helvetica", "bold")
    doc.setFontSize(13)
    doc.text("Por Prioridade", margin, y)
    y += 7
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)

    for (const [key, meta] of Object.entries(PRIORITY_META)) {
        const filtered = briefings.filter((b) => (b.priority || "normal") === key)
        const count = filtered.reduce((s, b) => s + (b.models?.length || 1), 0)
        if (count > 0) {
            checkPage()
            doc.setFont("helvetica", "bold")
            doc.text(`${meta.label}:`, margin, y)
            doc.setFont("helvetica", "normal")
            doc.text(String(count), margin + 50, y)
            y += 5
        }
    }

    addLine()

    // Lista resumida de todos os briefings
    doc.setFont("helvetica", "bold")
    doc.setFontSize(13)
    doc.text("Lista de Briefings", margin, y)
    y += 8

    for (const b of briefings) {
        checkPage(20)
        doc.setFont("helvetica", "bold")
        doc.setFontSize(10)
        doc.text(b.title || "Sem título", margin, y)
        y += 5
        doc.setFont("helvetica", "normal")
        doc.setFontSize(9)
        doc.setTextColor(100, 100, 100)
        const statusLabel = STATUS_META[b.status]?.label || b.status
        const modelLabel = b.models?.length
            ? (b.models.length > 1 ? `${b.models.length} modelagens` : b.models[0]?.model_name)
            : b.model_name
        const parts = [
            modelLabel,
            b.client_name ? `Cliente: ${b.client_name}` : null,
            b.designer_name ? `Designer: ${b.designer_name}` : null,
            b.deadline ? `Prazo: ${new Date(b.deadline + "T00:00:00").toLocaleDateString("pt-BR")}` : null,
            `Status: ${statusLabel}`,
        ].filter(Boolean)
        doc.text(parts.join("  ·  "), margin, y)
        doc.setTextColor(0, 0, 0)
        y += 7
    }

    // Rodapé
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text(`Gerado em ${new Date().toLocaleDateString("pt-BR")} · Aisten`, margin, 285)
        doc.text(`Página ${i}/${pageCount}`, 190, 285, { align: "right" })
    }

    doc.save("resumo-briefings.pdf")
}

function Info({ label, value }: { label: string; value?: string | null }) {
    if (!value) return null
    return (
        <div className="flex flex-col">
            <span className="text-[10px] text-primary/40">{label}</span>
            <span className="text-primary/80 font-medium">{value}</span>
        </div>
    )
}

// ── Área do designer: os únicos 2 campos que o designer responsável pode editar ──
function DesignerArea({ briefing, editable, onSaved }: { briefing: any; editable: boolean; onSaved: (updated: any) => void }) {
    const [notes, setNotes] = useState(briefing.designer_notes || "")
    const [link, setLink] = useState(briefing.designer_project_link || "")
    const [saving, setSaving] = useState(false)
    const [sending, setSending] = useState(false)
    const [dirty, setDirty] = useState(false)

    useEffect(() => {
        setNotes(briefing.designer_notes || "")
        setLink(briefing.designer_project_link || "")
        setDirty(false)
    }, [briefing.id])

    // Se nem é o designer responsável, nem tem nada preenchido ainda, não mostra a seção.
    if (!editable && !briefing.designer_notes && !briefing.designer_project_link) return null

    const handleSave = async () => {
        setSaving(true)
        try {
            const result = await updateBriefing(briefing.id, { designer_notes: notes, designer_project_link: link })
            if (result) {
                onSaved(result)
                setDirty(false)
            }
        } finally {
            setSaving(false)
        }
    }

    // O designer terminou a arte e manda de volta pro vendedor avaliar com o cliente.
    const myId = getUserId()
    const isAssignedDesigner = briefing.designer_id === myId
    const canSendForApproval = isAssignedDesigner && ["em_producao", "em_revisao"].includes(briefing.status)

    const handleSendForApproval = async () => {
        setSending(true)
        try {
            const result = await updateBriefing(briefing.id, {
                designer_notes: notes,
                designer_project_link: link,
                status: "aprovacao_cliente",
            })
            if (result) {
                onSaved(result)
                setDirty(false)
            }
        } finally {
            setSending(false)
        }
    }

    return (
        <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-3">
            <p className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Palette className="h-3 w-3" /> Área do designer
                {!editable && <span className="text-primary/30 normal-case font-normal">(somente leitura)</span>}
            </p>

            <div className="space-y-2">
                <div>
                    <label className="text-[10px] text-primary/40 block mb-1">Dúvidas e ajustes feitos</label>
                    {editable ? (
                        <textarea
                            value={notes}
                            onChange={(e) => { setNotes(e.target.value); setDirty(true) }}
                            placeholder="Anote aqui dúvidas pro vendedor ou o que foi ajustado na arte..."
                            className="w-full rounded-md border border-white/10 bg-secondary px-2.5 py-2 text-xs text-primary min-h-[70px] focus:outline-none focus:border-purple-500"
                        />
                    ) : (
                        <p className="text-xs text-primary/70 whitespace-pre-wrap">{briefing.designer_notes || "—"}</p>
                    )}
                </div>

                <div>
                    <label className="text-[10px] text-primary/40 block mb-1 flex items-center gap-1"><LinkIcon className="h-2.5 w-2.5" /> Link do projeto trabalhado</label>
                    {editable ? (
                        <input
                            type="url"
                            value={link}
                            onChange={(e) => { setLink(e.target.value); setDirty(true) }}
                            placeholder="https://..."
                            className="w-full rounded-md border border-white/10 bg-secondary px-2.5 py-2 text-xs text-primary focus:outline-none focus:border-purple-500"
                        />
                    ) : briefing.designer_project_link ? (
                        <a href={briefing.designer_project_link} target="_blank" rel="noreferrer" className="text-xs text-blue-400 hover:underline break-all">
                            {briefing.designer_project_link}
                        </a>
                    ) : (
                        <p className="text-xs text-primary/70">—</p>
                    )}
                </div>

                {editable && (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleSave}
                            disabled={saving || sending || !dirty}
                            className="flex items-center gap-1.5 rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 transition-colors disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Salvar
                        </button>
                        {canSendForApproval && (
                            <button
                                onClick={handleSendForApproval}
                                disabled={saving || sending}
                                className="flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
                            >
                                {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                Enviar para aprovação do cliente
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

// ── Dashboard simples: quantos briefings cada designer/vendedor tem e pendências ──
function BriefingsDashboard({ briefings }: { briefings: any[] }) {
    const OPEN_STATUSES = ["pendente", "em_producao", "em_revisao", "aprovacao_cliente", "falta_resposta", "pausado"]

    // Conta modelagens (models) ao invés de briefings
    const countModels = (list: any[]) => list.reduce((sum, b) => sum + (b.models?.length || 1), 0)

    const groupBy = (key: "designer_name" | "vendedor_name") => {
        const map = new Map<string, { total: number; pendentes: number }>()
        briefings.forEach((b) => {
            const name = b[key] || "Não atribuído"
            const n = b.models?.length || 1
            const entry = map.get(name) || { total: 0, pendentes: 0 }
            entry.total += n
            if (OPEN_STATUSES.includes(b.status)) entry.pendentes += n
            map.set(name, entry)
        })
        return Array.from(map.entries())
            .map(([name, v]) => ({ name, ...v }))
            .sort((a, b) => b.total - a.total)
    }

    const byDesigner = groupBy("designer_name")
    const byVendedor = groupBy("vendedor_name")

    const totalModelagens = countModels(briefings)

    const statusCounts = Object.keys(STATUS_META).map((k) => ({
        key: k,
        label: STATUS_META[k].label,
        color: STATUS_META[k].color,
        count: countModels(briefings.filter((b) => b.status === k)),
    }))

    return (
        <div className="border-b border-white/10 bg-background px-5 py-4 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider">Resumo</p>
                <button
                    onClick={() => generateDashboardPDF(briefings)}
                    className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-xs text-primary/60 hover:text-primary hover:bg-white/10 transition-colors"
                >
                    <Download className="h-3.5 w-3.5" /> Baixar PDF
                </button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status geral */}
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Status geral ({totalModelagens} modelagens)</p>
                    <div className="flex flex-wrap gap-1.5">
                        {statusCounts.map((s) => (
                            <span key={s.key} className={`rounded-full px-2.5 py-1 text-xs font-medium ${s.color}`}>
                                {s.label}: {s.count}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Por prioridade */}
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Por prioridade</p>
                    <div className="flex flex-wrap gap-1.5">
                        {Object.entries(PRIORITY_META).map(([k, v]) => {
                            const count = countModels(briefings.filter((b) => (b.priority || "normal") === k))
                            return (
                                <span key={k} className={`rounded-full px-2.5 py-1 text-xs font-medium bg-white/5 ${v.color}`}>
                                    {v.label}: {count}
                                </span>
                            )
                        })}
                    </div>
                </div>

                {/* Por designer */}
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Por designer</p>
                    <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
                        {byDesigner.map((d) => (
                            <div key={d.name} className="flex items-center justify-between text-xs bg-white/5 rounded-md px-2.5 py-1.5">
                                <span className="text-primary/80 truncate">{d.name}</span>
                                <span className="text-primary/50 flex-shrink-0 ml-2">{d.total} total · {d.pendentes} em aberto</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Por vendedor */}
                <div>
                    <p className="text-xs font-semibold text-primary/50 uppercase tracking-wider mb-2">Por vendedor</p>
                    <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
                        {byVendedor.map((v) => (
                            <div key={v.name} className="flex items-center justify-between text-xs bg-white/5 rounded-md px-2.5 py-1.5">
                                <span className="text-primary/80 truncate">{v.name}</span>
                                <span className="text-primary/50 flex-shrink-0 ml-2">{v.total} total · {v.pendentes} em aberto</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}